import { Injectable, computed, signal } from '@angular/core';
import {
  Auth,
  EmailAuthProvider,
  GoogleAuthProvider,
  MultiFactorError,
  MultiFactorInfo,
  MultiFactorResolver,
  TotpMultiFactorGenerator,
  TotpSecret,
  User,
  getMultiFactorResolver,
  multiFactor,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  sendEmailVerification,
} from '@angular/fire/auth';
import { Functions, httpsCallableData } from '@angular/fire/functions';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { firstValueFrom } from 'rxjs';

import {
  MFA_TOTP_ISSUER,
  MfaEnforcementState,
  resolveGracePeriodDays,
} from '../models/mfa.model';

export interface TotpEnrollmentStart {
  secret: TotpSecret;
  qrCodeUrl: string;
  secretKey: string;
}

export interface TotpReauthenticationChallenge {
  hints: MultiFactorInfo[];
}

export interface ClearMfaFactorsResult {
  success: boolean;
  /** How many factors were removed; 0 when the user had none enrolled. */
  clearedFactorCount: number;
  graceUntil: number;
}

interface CohortRule {
  enabled: boolean;
  startAt: number | null;
}

/** The parts of `settings/current.mfa` the client needs, read once per session. */
interface MfaSettingsSnapshot {
  allUsers: CohortRule;
  gracePeriodDays: number;
  trustedSsoDomains: string[];
}

const MFA_REQUIRED_CLAIM = 'mfaRequired';
const GOOGLE_PROVIDER_ID = 'google.com';

function normalizeDomains(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter((entry): entry is string => typeof entry === 'string')
    .map((entry) => entry.trim().toLowerCase().replace(/^@/, ''))
    .filter((entry) => entry.length > 0);
}

function toMillis(value: unknown): number | null {
  if (value && typeof (value as { toMillis?: unknown }).toMillis === 'function') {
    return (value as { toMillis: () => number }).toMillis();
  }
  return typeof value === 'number' ? value : null;
}

function parseCohort(raw: unknown): CohortRule {
  if (!raw || typeof raw !== 'object') {
    return { enabled: false, startAt: null };
  }
  const rule = raw as Record<string, unknown>;
  return { enabled: rule['enabled'] === true, startAt: toMillis(rule['startAt']) };
}

@Injectable({ providedIn: 'root' })
export class MfaService {
  private readonly statusSignal = signal<MfaEnforcementState | null>(null);
  private statusForUid: string | null = null;
  private inFlight: Promise<MfaEnforcementState | null> | null = null;

  readonly status = computed(() => this.statusSignal());
  /** Subject, un-enrolled and past the deadline — block the app. */
  readonly mustEnrol = computed(() => this.statusSignal()?.mustEnrol === true);
  /** Subject, un-enrolled and within grace — prompt but allow deferral. */
  readonly shouldPrompt = computed(() => this.statusSignal()?.shouldPrompt === true);

  private mfaSettings: MfaSettingsSnapshot | null = null;
  private pendingReauthResolver: MultiFactorResolver | null = null;
  private recentlyReauthenticatedUser: User | null = null;

  constructor(
    private auth: Auth,
    private functions: Functions,
    private firestore: Firestore,
  ) {}

  // Reads the public enforcement schedule, cached for the session. Used to
  // decide whether a non-privileged user could yet be subject to enforcement
  // without an unconditional Cloud Function round-trip, to phrase deadline copy
  // from config, and to mirror the rules' trusted-SSO exemption offline.
  private async getMfaSettings(): Promise<MfaSettingsSnapshot> {
    if (!this.mfaSettings) {
      try {
        const snap = await getDoc(doc(this.firestore, 'settings/current'));
        const mfa = (snap.data()?.['mfa'] ?? {}) as Record<string, unknown>;
        this.mfaSettings = {
          allUsers: parseCohort(mfa['allUsers']),
          gracePeriodDays: resolveGracePeriodDays(mfa['gracePeriodDays']),
          trustedSsoDomains: normalizeDomains(mfa['trustedSsoDomains']),
        };
      } catch {
        this.mfaSettings = {
          allUsers: { enabled: false, startAt: null },
          gracePeriodDays: resolveGracePeriodDays(undefined),
          trustedSsoDomains: [],
        };
      }
    }
    return this.mfaSettings;
  }

  private requireUser(): User {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('No authenticated user.');
    }
    return user;
  }

  private userForSensitiveOperation(): User {
    const user = this.requireUser();
    if (this.recentlyReauthenticatedUser?.uid === user.uid) {
      return this.recentlyReauthenticatedUser;
    }
    this.recentlyReauthenticatedUser = null;
    return user;
  }

  getEnrolledFactors(): MultiFactorInfo[] {
    const user = this.auth.currentUser;
    return user ? multiFactor(user).enrolledFactors : [];
  }

  isEnrolled(): boolean {
    return this.getEnrolledFactors().length > 0;
  }

  isEmailVerified(): boolean {
    return this.auth.currentUser?.emailVerified === true;
  }

  getCurrentUserEmail(): string | null {
    return this.auth.currentUser?.email ?? null;
  }

  async sendVerificationEmail(): Promise<void> {
    const user = this.requireUser();
    await sendEmailVerification(user, { url: window.location.href });
  }

  async reloadCurrentUser(): Promise<User> {
    const user = this.requireUser();
    await user.reload();
    await user.getIdToken(true).catch(() => undefined);
    return user;
  }

  // Whether the current user can skip the server round-trip: they hold no role
  // subject to the 2FA requirement, and no hard-block claim that only the server
  // can clear. Read straight from the (cached) ID token, so no network call.
  private async canSkipServerStatus(user: User): Promise<boolean> {
    const { claims } = await user.getIdTokenResult();
    const roles = Array.isArray(claims['roles']) ? claims['roles'] as string[] : [];
    const privileged = claims['isAdmin'] === true || roles.includes('group-admin');

    // A user still carrying `mfaRequired` is blocked by the rules even if their
    // cohort has since been disabled. Only the server clears that claim, so they
    // must go to it rather than short-circuit to a "not required" state the
    // rules would contradict.
    return !privileged && claims[MFA_REQUIRED_CLAIM] !== true;
  }


  private notRequiredState(gracePeriodDays: number, requiredFrom: number | null = null): MfaEnforcementState {
    return {
      enforcementEnabled: false,
      required: false,
      requiredFrom,
      enrolled: this.isEnrolled(),
      satisfiedBySso: false,
      graceUntil: null,
      gracePeriodDays,
      daysRemaining: 0,
      graceExpired: false,
      mustEnrol: false,
      shouldPrompt: false,
    };
  }

  // Whether this session inherits the trusted-domain Google SSO exemption, using
  // the same test as `signedInWithTrustedSso()` in the Firestore/Storage rules:
  // the current sign-in must be Google, on a configured domain.
  private isTrustedSsoSession(user: User, trustedSsoDomains: string[]): boolean {
    if (!trustedSsoDomains.length || !user.emailVerified) {
      return false;
    }

    const email = user.email?.trim().toLowerCase() ?? '';
    const atIndex = email.lastIndexOf('@');

    return atIndex >= 0
      && trustedSsoDomains.includes(email.slice(atIndex + 1))
      && user.providerData.some((provider) => provider.providerId === GOOGLE_PROVIDER_ID);
  }

  // Fallback state derived from the ID token when the status callable fails.
  //
  // The `mfaRequired` claim is what the Firestore and Storage rules actually
  // enforce, so a user carrying it is blocked server-side whether or not we can
  // reach the callable. Letting them into the app on a transient error would only
  // trade the guided setup gate for cryptic permission-denied errors, so we fail
  // closed on the claim. Users without it are unaffected — the rules would not
  // block them either, so there is nothing to gate.
  private async claimFallbackState(user: User): Promise<MfaEnforcementState | null> {
    const { claims } = await user.getIdTokenResult();

    // No hard-block claim: the rules would not block this user either, so leave
    // whatever status we already hold (a live grace countdown, say) untouched.
    if (claims[MFA_REQUIRED_CLAIM] !== true) {
      return null;
    }

    const { gracePeriodDays, trustedSsoDomains } = await this.getMfaSettings();

    // The claim is set but the requirement is already met locally — they have
    // just enrolled, or the session is SSO-exempt. Report unblocked so a stale
    // cached block cannot bounce them back to the gate they just cleared; the
    // server drops the claim on its next successful reconcile.
    if (this.isEnrolled() || this.isTrustedSsoSession(user, trustedSsoDomains)) {
      return this.notRequiredState(gracePeriodDays);
    }

    return {
      enforcementEnabled: true,
      required: true,
      requiredFrom: null,
      enrolled: false,
      satisfiedBySso: false,
      graceUntil: null,
      gracePeriodDays,
      daysRemaining: 0,
      graceExpired: true,
      mustEnrol: true,
      shouldPrompt: false,
    };
  }

  // Re-fetches enforcement status from the server (which also stamps the grace
  // deadline and reconciles the mfaRequired claim), caching it per user. The ID
  // token is force-refreshed afterwards so Firestore rules see any claim change.
  // Non-privileged users with no hard-block claim are not subject to enforcement
  // until the all-users cohort starts, so they short-circuit to a local "not
  // required" state without any Cloud Function round-trip. If the call fails, the
  // state falls back to the ID token's claim rather than letting a blocked user
  // through — see claimFallbackState().
  async refreshStatus(force = false): Promise<MfaEnforcementState | null> {
    const user = this.auth.currentUser;
    if (!user) {
      this.clear();
      return null;
    }

    if (!force && this.statusForUid === user.uid && this.statusSignal() !== null) {
      return this.statusSignal();
    }

    if (this.inFlight) {
      return this.inFlight;
    }

    // Non-privileged users are only ever subject once the all-users cohort has
    // started. Until then, skip the Cloud Function entirely and surface the
    // scheduled start (if any) so the UI can still nudge them.
    if (await this.canSkipServerStatus(user)) {
      const { allUsers, gracePeriodDays } = await this.getMfaSettings();
      const now = Date.now();
      const started = allUsers.enabled && (allUsers.startAt === null || now >= allUsers.startAt);

      if (!started) {
        const requiredFrom = allUsers.enabled && allUsers.startAt !== null && now < allUsers.startAt
          ? allUsers.startAt
          : null;
        const state = this.notRequiredState(gracePeriodDays, requiredFrom);
        this.statusSignal.set(state);
        this.statusForUid = user.uid;
        return state;
      }
      // All-users enforcement has started — fall through to the authoritative
      // server status like any subject user.
    }

    this.inFlight = (async () => {
      try {
        const callable = httpsCallableData<Record<string, never>, MfaEnforcementState>(
          this.functions,
          'getMfaEnforcementStatus',
        );
        const state = await firstValueFrom(callable({}));
        this.statusSignal.set(state);
        this.statusForUid = user.uid;
        await user.getIdToken(true).catch(() => undefined);
        return state;
      } catch (error) {
        console.error('[MfaService] Failed to load enforcement status:', error);

        // Fail closed on the hard-block claim rather than falling through to a
        // possibly stale (or absent) status, which would let a locked-out user
        // past the gate and into permission-denied errors instead.
        const fallback = await this.claimFallbackState(user).catch(() => null);
        if (fallback) {
          this.statusSignal.set(fallback);
          this.statusForUid = user.uid;
          return fallback;
        }

        return this.statusSignal();
      } finally {
        this.inFlight = null;
      }
    })();

    return this.inFlight;
  }

  clear(): void {
    this.statusSignal.set(null);
    this.statusForUid = null;
  }

  // Generates a new TOTP secret and matching QR code for enrollment. Requires an
  // authenticated user; does not require recent login (enroll() does).
  async beginTotpEnrollment(): Promise<TotpEnrollmentStart> {
    const user = this.userForSensitiveOperation();
    try {
      const session = await multiFactor(user).getSession();
      const secret = await TotpMultiFactorGenerator.generateSecret(session);
      const accountName = user.email ?? user.uid;
      const qrCodeUrl = secret.generateQrCodeUrl(accountName, MFA_TOTP_ISSUER);
      return { secret, qrCodeUrl, secretKey: secret.secretKey };
    } catch (error) {
      if ((error as { code?: string })?.code === 'auth/maximum-second-factor-count-exceeded') {
        await user.reload().catch(() => undefined);
      }
      throw error;
    }
  }

  // Verifies the user's first TOTP code and enrols the factor.
  async completeTotpEnrollment(secret: TotpSecret, oneTimeCode: string, displayName: string): Promise<void> {
    const user = this.userForSensitiveOperation();
    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, oneTimeCode.trim());
    await multiFactor(user).enroll(assertion, displayName);
    this.recentlyReauthenticatedUser = user;
    await this.refreshStatus(true);
  }

  async unenroll(factorUid: string): Promise<void> {
    const user = this.userForSensitiveOperation();
    await multiFactor(user).unenroll(factorUid);
    this.recentlyReauthenticatedUser = user;
    await this.refreshStatus(true);
  }

  // Whether the current user can re-authenticate with a password. Google-only
  // accounts (TRACit staff, who must sign in with Google) have no password
  // credential, so they re-authenticate through the provider instead.
  usesPasswordCredential(): boolean {
    const user = this.auth.currentUser;
    return !!user?.providerData.some((provider) => provider.providerId === 'password');
  }

  private totpReauthenticationChallenge(error: unknown): TotpReauthenticationChallenge | null {
    if ((error as { code?: string })?.code !== 'auth/multi-factor-auth-required') {
      return null;
    }

    this.pendingReauthResolver = getMultiFactorResolver(this.auth, error as MultiFactorError);
    const totpHints = this.pendingReauthResolver.hints.filter(
      (hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID,
    );

    return { hints: totpHints.length ? totpHints : this.pendingReauthResolver.hints };
  }

  // Re-authenticates the current user with their password. If the account already
  // has MFA, the password is accepted and Firebase returns a second-factor
  // challenge that the caller must resolve with an existing TOTP code.
  async reauthenticateWithPassword(password: string): Promise<TotpReauthenticationChallenge | null> {
    const user = this.requireUser();
    if (!user.email) {
      throw new Error('This account has no email address to re-authenticate with.');
    }
    const credential = EmailAuthProvider.credential(user.email, password);
    try {
      const userCredential = await reauthenticateWithCredential(user, credential);
      this.recentlyReauthenticatedUser = userCredential.user;
      this.pendingReauthResolver = null;
      return null;
    } catch (error) {
      const challenge = this.totpReauthenticationChallenge(error);
      if (challenge) {
        return challenge;
      }
      throw error;
    }
  }

  // Re-authenticates a federated (Google) user through the provider. Used in
  // place of the password prompt for accounts with no password credential.
  async reauthenticateWithGoogle(): Promise<TotpReauthenticationChallenge | null> {
    const user = this.requireUser();
    const provider = new GoogleAuthProvider();

    if (user.email) {
      provider.setCustomParameters({ login_hint: user.email });
    }

    try {
      const userCredential = await reauthenticateWithPopup(user, provider);
      this.recentlyReauthenticatedUser = userCredential.user;
      this.pendingReauthResolver = null;
      return null;
    } catch (error) {
      const challenge = this.totpReauthenticationChallenge(error);
      if (challenge) {
        return challenge;
      }
      throw error;
    }
  }

  async resolveTotpReauthentication(factorUid: string, oneTimeCode: string): Promise<void> {
    if (!this.pendingReauthResolver) {
      throw new Error('No pending multi-factor challenge.');
    }

    const assertion = TotpMultiFactorGenerator.assertionForSignIn(factorUid, oneTimeCode.trim());
    const userCredential = await this.pendingReauthResolver.resolveSignIn(assertion);
    this.recentlyReauthenticatedUser = userCredential.user;
    this.pendingReauthResolver = null;
  }

  // Renders an otpauth:// enrollment URL to a scannable QR-code data URL,
  // mirroring the promo-code QR flow. Returns null if generation fails so the
  // caller can fall back to manual setup-key entry.
  async generateQrDataUrl(otpAuthUrl: string): Promise<string | null> {
    try {
      const callable = httpsCallableData<{ otpAuthUrl: string }, { dataUrl: string }>(
        this.functions,
        'generateMfaQr',
      );
      const result = await firstValueFrom(callable({ otpAuthUrl }));
      return result.dataUrl ?? null;
    } catch (error) {
      console.error('[MfaService] Failed to generate MFA QR code:', error);
      return null;
    }
  }

  // System-admin-only: resets another user's grace deadline and clears any lockout.
  async resetGraceForUser(userId: string): Promise<{ success: boolean; graceUntil: number }> {
    const callable = httpsCallableData<{ userId: string }, { success: boolean; graceUntil: number }>(
      this.functions,
      'resetMfaGracePeriod',
    );
    return firstValueFrom(callable({ userId }));
  }

  // System-admin-only: removes another user's enrolled second factors and gives
  // them a fresh grace window. The recovery path for a lost authenticator, which
  // the user cannot resolve themselves — they are stopped at sign-in and never
  // reach the app. Verify who they are out of band before calling this.
  async clearFactorsForUser(userId: string): Promise<ClearMfaFactorsResult> {
    const callable = httpsCallableData<{ userId: string }, ClearMfaFactorsResult>(
      this.functions,
      'clearUserMfaFactors',
    );
    return firstValueFrom(callable({ userId }));
  }
}
